import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import LangSwitcher from '@/components/LangSwitcher';
import GoogleAuthButton from '@/components/GoogleAuthButton';
import { trackEvent } from '@/lib/analytics';

const TEXTURE = '/garde-valley.jpg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LEN = 10;

function estimatePasswordStrength(pwd) {
    if (!pwd) return { score: 0, label: '', pct: 0, cls: 'bg-zinc-200' };
    let s = 0;
    if (pwd.length >= 8) s += 1;
    if (pwd.length >= 12) s += 1;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s += 1;
    if (/[0-9]/.test(pwd)) s += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) s += 1;
    const lower = pwd.toLowerCase();
    if (/^(password|azerty|qwerty|123456|111111|admin|welcome|valley|african)/.test(lower)) s = Math.max(0, s - 2);
    if (/(.)\1{2,}/.test(pwd)) s = Math.max(0, s - 1);
    s = Math.min(4, s);
    const labels = ['Très faible', 'Faible', 'Moyen', 'Fort', 'Excellent'];
    const cls = ['bg-rose-400', 'bg-amber-400', 'bg-yellow-400', 'bg-lime-500', 'bg-emerald-600'][s];
    return { score: s, label: labels[s], pct: ((s + 1) / 5) * 100, cls };
}

export default function SignUp() {
    const { t } = useLang();
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const inviteToken = params.get('invite');

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [invite, setInvite] = useState(null);
    const [inviteChecking, setInviteChecking] = useState(false);

    useEffect(() => {
        if (!inviteToken) return;
        setInviteChecking(true);
        axios.get(`${API}/billing/stripe/invite/${encodeURIComponent(inviteToken)}`)
            .then((r) => {
                const d = r.data || {};
                if (d.valid) {
                    setInvite(d);
                    if (d.email) setEmail(d.email);
                    if (d.customer_name) setFullName(d.customer_name);
                } else {
                    setInvite({ valid: false });
                }
            })
            .catch(() => setInvite({ valid: false }))
            .finally(() => setInviteChecking(false));
    }, [inviteToken]);

    const strength = useMemo(() => estimatePasswordStrength(password), [password]);
    const canSubmit = fullName.trim() && email.includes('@') && password.length >= MIN_LEN && strength.score >= 2;

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password.length < MIN_LEN) {
            setError(`Mot de passe trop court (minimum ${MIN_LEN} caractères)`);
            return;
        }
        if (strength.score < 2) {
            setError(`Mot de passe trop faible (${strength.label}). Mélangez majuscules, chiffres et symboles.`);
            return;
        }
        setLoading(true);
        trackEvent('signup_started', { has_invite: !!inviteToken });
        try {
            const body = { email, password, full_name: fullName };
            if (inviteToken) body.invitation_token = inviteToken;
            await axios.post(`${API}/auth/signup`, body);
            const { error: si } = await signIn({ email, password });
            if (si) {
                setError(si.message || t('auth.error'));
                setLoading(false);
                return;
            }
            trackEvent('signup_completed', { plan: invite?.plan_id || 'free' });
            setLoading(false);
            navigate('/app/onboarding');
        } catch (err) {
            setError(err.response?.data?.detail || err.message || t('auth.error'));
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex relative overflow-hidden items-center justify-center bg-[#1a1612]" data-testid="signup-visual">
                <div className="absolute inset-0 opacity-90" style={{
                    backgroundImage:
                        'radial-gradient(80% 60% at 50% 0%, rgba(212,114,47,0.18) 0%, rgba(26,22,18,0) 65%), ' +
                        'radial-gradient(70% 60% at 50% 100%, rgba(57,99,76,0.22) 0%, rgba(26,22,18,0) 65%), ' +
                        'linear-gradient(180deg, #1f1a16 0%, #14110e 100%)',
                }} />
                <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
                <div className="absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-secondary/20 blur-3xl" />
                <Link to="/" className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 text-background/80 hover:text-background text-sm">
                    ← Retour à l'accueil
                </Link>
                <div className="relative z-10 flex flex-col items-center gap-7 px-10 py-12 max-w-[520px]">
                    <div className="relative w-full">
                        <div className="absolute -inset-4 bg-gradient-to-br from-primary/30 via-secondary/20 to-transparent rounded-[28px] blur-xl opacity-70" />
                        <div className="relative rounded-2xl overflow-hidden ring-1 ring-background/15 shadow-2xl">
                            <img src={TEXTURE} alt="African Valley" className="w-full h-auto object-contain block select-none" draggable={false} />
                            <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none bg-gradient-to-t from-black/40 to-transparent" />
                        </div>
                    </div>
                    <div className="text-center text-background/85 max-w-md">
                        <div className="eyebrow text-primary/90 mb-2">Plateforme africaine 360°</div>
                        <h2 className="font-heading text-2xl leading-snug">
                            Rejoignez la communauté <span className="text-primary">African Valley</span>.
                        </h2>
                    </div>
                </div>
            </div>

            <div className="flex flex-col">
                <div className="flex justify-between items-center px-8 lg:px-16 py-6">
                    <Link to="/" className="lg:hidden font-heading text-2xl">African <span className="text-primary">Valley</span></Link>
                    <div className="ml-auto"><LangSwitcher /></div>
                </div>
                <div className="flex-1 flex items-center px-8 lg:px-16 pb-12">
                    <div className="w-full max-w-md mx-auto">
                        {inviteToken && (
                            <div className="mb-6">
                                {inviteChecking ? (
                                    <div className="flex items-center gap-2 text-sm text-foreground/65">
                                        <Loader2 size={14} className="animate-spin" /> Vérification de votre lien d'activation…
                                    </div>
                                ) : invite?.valid ? (
                                    <div className="rounded-xl p-4 bg-primary-light border border-primary/20" data-testid="signup-invite-valid">
                                        <div className="flex items-start gap-3">
                                            <CheckCircle2 className="text-primary mt-0.5" size={20} />
                                            <div className="text-sm">
                                                <div className="font-semibold text-foreground">Plan <span className="text-primary">{invite.plan_name}</span> activé</div>
                                                <div className="text-foreground/70 mt-0.5">
                                                    Paiement de {invite.amount?.toLocaleString('fr-FR')} {invite.currency} validé.
                                                    Créez votre compte pour activer votre espace.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl p-4 bg-amber-50 border border-amber-200" data-testid="signup-invite-invalid">
                                        <div className="flex items-start gap-3">
                                            <AlertCircle className="text-amber-600 mt-0.5" size={20} />
                                            <div className="text-sm">
                                                <div className="font-semibold">Lien d'activation invalide ou expiré</div>
                                                <div className="text-foreground/70 mt-0.5">
                                                    Vous pouvez tout de même créer un compte, ou
                                                    {' '}<Link to="/p/contact" className="text-primary hover:underline">nous contacter</Link>.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="eyebrow mb-3">{invite?.valid ? 'Activation' : 'Inscription'}</div>
                        <h1 className="font-heading text-4xl mb-8">{t('auth.signup_title')}</h1>

                        <GoogleAuthButton label="Continuer avec Google" testid="signup-google-btn" />

                        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-foreground/45">
                            <div className="flex-1 h-px bg-border" />
                            <span>ou avec votre email</span>
                            <div className="flex-1 h-px bg-border" />
                        </div>

                        <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
                            <div className="space-y-2">
                                <Label htmlFor="full_name">{t('auth.full_name')}</Label>
                                <Input id="full_name" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-12" data-testid="signup-fullname" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('auth.email')}</Label>
                                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" placeholder="vous@organisation.com" data-testid="signup-email" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="flex items-center justify-between">
                                    <span>{t('auth.password')}</span>
                                    <span className="text-[11px] text-foreground/55 inline-flex items-center gap-1">
                                        <ShieldCheck size={11} /> {MIN_LEN} caractères minimum
                                    </span>
                                </Label>
                                <Input id="password" type="password" required minLength={MIN_LEN} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="signup-password" />
                                {password.length > 0 && (
                                    <div className="space-y-1.5" data-testid="signup-password-strength">
                                        <div className="h-1.5 rounded-full bg-zinc-200 overflow-hidden">
                                            <div className={`h-full transition-all duration-300 ${strength.cls}`} style={{ width: `${strength.pct}%` }} />
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className={strength.score >= 3 ? 'text-emerald-700 font-medium' : 'text-foreground/60'}>
                                                Sécurité : {strength.label || '—'}
                                            </span>
                                            <span className="text-foreground/40">
                                                {password.length < MIN_LEN ? `${MIN_LEN - password.length} car. requis` : ''}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-foreground/55 leading-relaxed">
                                            Astuce : combinez majuscules, minuscules, chiffres et un symbole.
                                        </div>
                                    </div>
                                )}
                            </div>
                            {error && (
                                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="signup-error">
                                    {error}
                                </div>
                            )}
                            <Button type="submit" disabled={loading || !canSubmit}
                                className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground"
                                data-testid="signup-submit">
                                {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.submit_signup')}
                            </Button>
                        </form>

                        <div className="mt-8 text-center">
                            <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-primary transition-colors" data-testid="link-to-signin">
                                {t('auth.switch_to_signin')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
